// Spaceship.js
import React, { useRef, useState } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import {
  useGLTF,
  Environment,
  OrbitControls,
  Text,
  MeshTransmissionMaterial
} from "@react-three/drei";
import { useNavigate } from "react-router-dom";
import "./Spaceship.css";

// 🔊 飞出音效
const takeoffSound = new Audio("/sounds/launch.mp3");

// —— 纯三维按钮组件 ——  
function ThreeButton({
  position,
  label,
  onClick,
  size = [6, 0.6],
  fontSize = 0.3
}) {
  const meshRef = useRef();
  return (
    <group position={position}>
      <mesh
        ref={meshRef}
        onClick={(e) => {
          e.stopPropagation();
          onClick();
        }}
        onPointerOver={() => (document.body.style.cursor = "pointer")}
        onPointerOut={() => (document.body.style.cursor = "auto")}
      >

        <planeGeometry args={size} />
        <MeshTransmissionMaterial
          color="none"           // 深灰底色
          
          samples={20}              // 抗锯齿采样，越高越平滑
          resolution={512}          // 背景渲染分辨率
          roughness={0.9}           // 毛玻璃程度 (0=干净 1=全雾)
          transmission={1}        // 透光度 (0~1)
          thickness={2}             // 玻璃厚度，影响色散
          ior={1.5}                 // 折射率
          chromaticAberration={3}// 色差效果
          anisotropy={2}          // 各向异性折射
          // envMapIntensity 不需要，因为它不使用环境贴图反射
        />
      </mesh>
      <Text
        position={[1.7, 0, 0.01]}
        fontSize={fontSize}
        color="#ffffff"
        anchorX="right"
        anchorY="middle"
        
      >
        {label}
      </Text>
    </group>
  );
}

// —— 飞船模型 + 动画逻辑 ——  
function RotatingModel({ url, animate, onClick, onAnimationComplete }) {
  const ref = useRef();
  const { scene } = useGLTF(url);
  scene.scale.set(0.2, 0.2, 0.2);

  useFrame((state, delta) => {
    const t = state.clock.getElapsedTime();
    if (!ref.current) return;

    if (!animate.active) {
      // 悬浮 & 旋转
      ref.current.rotation.x = 0.3 * Math.sin(t * 0.7);
      ref.current.rotation.y = t * 0.4;
      ref.current.rotation.z = 0.3 * Math.sin(t * 0.5);
      ref.current.position.y = 0.1 * Math.sin(t * 1.5);
      ref.current.position.z = 0;
    } else {
      // 起飞动画
      animate.elapsed += delta;
      if (animate.elapsed < 0.5) {
        ref.current.translateZ(-delta * 1.5);
        ref.current.rotation.x += delta * 0.3 * Math.sin(t * 15);
        ref.current.rotation.z += delta * 0.3 * Math.sin(t * 20);
      }
      if (animate.elapsed > 0.8) {
        onAnimationComplete();
        animate.active = false;
      }
    }
  });

  return (
    <group
      ref={ref}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
    >
      <primitive object={scene} />
    </group>
  );
}

export default function Spaceship({ url = "/models/spaceship.glb" }) {
  const navigate = useNavigate();
  const [showText, setShowText] = useState(false);
  const animate = useRef({ active: false, elapsed: 0 });

  const handleLaunch = () => {
    if (!animate.current.active) {
      animate.current.active = true;
      animate.current.elapsed = 0;
      setShowText(true);
      takeoffSound.currentTime = 0;
      takeoffSound.play();
    }
  };

  // 1. 按钮列表
  const buttons = [
    { label: "Burner Chat",   onClick: () => navigate("/burnerchat") },
{ label: "Burner Call", onClick: () => navigate("/call") },

    { label: "Mail",        onClick: () => navigate("/mail") },
   
 
     { label: "Me",    onClick: () => navigate("/more") },
        { label: "Gate",    onClick: () => navigate("/gate") },
       { label: "Work-place",        onClick: () => navigate("/work") },
    { label: "Game-center ",        onClick: () => navigate("/game") },

  ];

  // 2. 布局控制：按钮高度 & 上下留白
  const btnHeight = 0.6;
  const startY = ((buttons.length + 2) * btnHeight) / 2;

  return (
    <div
      style={{
        position: "fixed",
        bottom: "50px",
        right: "20px",
        width: "200px",
        height: "200px",
        zIndex: 30,
        background: "none",
      }}
    >
      <Canvas
        style={{ width: "100%", height: "100%" }}
        camera={{ position: [5, 0, 9], fov: 35 }}
        gl={{ alpha: true }}
      >
        {/* 环境光 + HDRI（用于飞船 & 场景照明，但按钮不会反射它） */}
        <ambientLight intensity={0.5} />
        <directionalLight position={[2, 2, 2]} intensity={0.7} />
        <Environment preset="city" background={false} />

        {/* 飞船模型 */}
        <RotatingModel
          url={url}
          animate={animate.current}
          onClick={handleLaunch}
          onAnimationComplete={() =>
            navigate(
              window.location.pathname === "/gallery" ? "/" : "/gallery"
            )
          }
        />

        {/* 纯三维按钮 */}
        {buttons.map((btn, i) => (
          <ThreeButton
            key={i}
            position={[
              1,                         // X 轴固定
              startY - (i + 1) * btnHeight, // Y 轴：留白 + 排列
              0,
            ]}
            size={[4, btnHeight]}
            fontSize={0.4}
            label={btn.label}
            onClick={btn.onClick}
          />
        ))}

        <OrbitControls enableZoom={false} />
      </Canvas>

      {showText && (
        <div
          style={{
            position: "absolute",
            bottom: "-24px",
            right: "0",
            color: "#0ff",
            fontSize: "14px",
            fontFamily: "monospace",
            textShadow: "0 0 5px #0ff",
          }}
        >
          Let's go...
        </div>
      )}
    </div>
  );
}
